/**
 * GamificationCelebration - Modal de celebração quando usuário atinge novo estágio
 */
import React, { useEffect, useState } from 'react';
import { Award, Sparkles, Trophy, Star, Zap } from 'lucide-react';

const STAGE_MESSAGES = {
  1: {
    title: '🌱 Sua Acácia Brotou!',
    message: 'Você completou sua primeira ilha! Sua jornada de crescimento começou.',
    color: '#10B981',
    icon: Sparkles,
  },
  2: {
    title: '🌿 Crescimento Visível!',
    message: 'Duas ilhas completas! Sua Acácia está se desenvolvendo lindamente.',
    color: '#0F6E56',
    icon: Star,
  },
  3: {
    title: '🍃 Folhas Abundantes!',
    message: 'Três ilhas conquistadas! Sua dedicação está dando frutos.',
    color: '#8B5CF6',
    icon: Zap,
  },
  4: {
    title: '🌳 Acácia Plena!',
    message: 'Jornada completa! Você dominou todas as ilhas. Sua Acácia atingiu o estágio máximo!',
    color: '#C8541A',
    icon: Trophy,
  },
};

export default function GamificationCelebration({ stage, onClose }) {
  const [show, setShow] = useState(false);
  const [confetti, setConfetti] = useState([]);
  
  useEffect(() => {
    if (stage > 0) {
      setShow(true);
      // Generate confetti particles
      const particles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
      }));
      setConfetti(particles);
    }
  }, [stage]);
  
  if (!show || !stage || stage === 0) return null;
  
  const config = STAGE_MESSAGES[stage] || STAGE_MESSAGES[1];
  const Icon = config.icon;
  
  const handleClose = () => {
    setShow(false);
    if (onClose) onClose();
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={handleClose}
    >
      {/* Confetti */}
      {confetti.map((particle) => (
        <div
          key={particle.id}
          className="absolute w-2 h-2 rounded-full animate-confetti"
          style={{
            left: `${particle.left}%`,
            top: '-10px',
            background: ['#F59E0B', '#10B981', '#8B5CF6', '#EF4444'][particle.id % 4],
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`,
          }}
        />
      ))}
      
      {/* Modal */}
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center transform animate-scaleIn relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Background decoration */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${config.color}, transparent 70%)`,
          }}
        />
        
        {/* Content */}
        <div className="relative z-10">
          {/* Icon */}
          <div 
            className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg animate-bounce"
            style={{ background: `${config.color}15`, border: `3px solid ${config.color}` }}
          >
            <Icon className="w-12 h-12" style={{ color: config.color }} />
          </div>
          
          {/* Title */}
          <h2 
            className="text-3xl font-bold mb-3"
            style={{ color: config.color }}
          >
            {config.title}
          </h2>
          
          {/* Message */}
          <p className="text-gray-700 text-lg mb-6 leading-relaxed">
            {config.message}
          </p>
          
          {/* Stage indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-all ${
                  s <= stage ? 'scale-110' : 'opacity-30'
                }`}
                style={{ 
                  background: s <= stage ? config.color : '#D1D5DB',
                }}
              />
            ))}
          </div>
          
          {/* CTA */}
          <button
            onClick={handleClose}
            className="px-8 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            style={{ background: config.color }}
          >
            Continuar Jornada
          </button>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.8) translateY(20px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
